import { useCallback } from "react"
import { useMutation } from "urql"

import type {
  CancelQualityApprovalMutation,
  QualityApprovalCancelFailure,
  QualityApprovalCancelInput,
} from "@/graphql/codegen/graphql"
import { CancelQualityApprovalDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

export interface UseCancelQualityApprovalReturn {
  cancelQualityApproval: (input: QualityApprovalCancelInput) => Promise<boolean>
  isCanceling: boolean
}

export const useCancelQualityApproval = (): UseCancelQualityApprovalReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching }, executeMutation] = useMutation<
    CancelQualityApprovalMutation,
    { input: QualityApprovalCancelInput }
  >(CancelQualityApprovalDocument)

  const cancelQualityApproval = useCallback(
    async (input: QualityApprovalCancelInput): Promise<boolean> => {
      const { data, error } = await executeMutation({ input })
      const output = data?.qualityApproval.cancel
      const hasError = error || output?.__typename === "QualityApprovalCancelFailure"

      if (hasError) {
        const message = error?.message ?? (output as QualityApprovalCancelFailure).error.message
        notify({ type: NotificationType.Error, message })

        return false
      }

      notify({ type: NotificationType.Success, message: "Out of spec approval request canceled" })

      return true
    },
    [executeMutation, notify]
  )

  return { cancelQualityApproval, isCanceling: fetching }
}
